import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiPause, FiUpload, FiHeadphones, FiVolume2, FiTrash2, FiVideo, FiMusic } = FiIcons;

const Demo = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState('https://mzunalhoitapgthoksjn.supabase.co/storage/v1/object/public/site-assets/riverside_ben_linkedin_website.mp3');
  const [useDefaultDemo, setUseDefaultDemo] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [currentPlayingFile, setCurrentPlayingFile] = useState(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  // File size limit: 50MB
  const MAX_FILE_SIZE = 50 * 1024 * 1024;
  const ACCEPTED_TYPES = {
    'audio/wav': '.wav',
    'video/mp4': '.mp4',
    'video/quicktime': '.mov'
  };

  // Listen for the custom play event from Header
  useEffect(() => {
    const handleTriggerPlay = () => {
      // Only trigger play if not currently playing a video
      if (!currentPlayingFile?.isVideo && !isPlaying) {
        togglePlayback();
      }
    };

    window.addEventListener('triggerDemoPlay', handleTriggerPlay);
    
    return () => {
      window.removeEventListener('triggerDemoPlay', handleTriggerPlay);
    };
  }, [isPlaying, currentPlayingFile]);

  const validateFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };
    
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'File size must be less than 50MB' };
    }

    const isValidType = Object.keys(ACCEPTED_TYPES).includes(file.type) || 
                       file.name.toLowerCase().endsWith('.wav') ||
                       file.name.toLowerCase().endsWith('.mp4') ||
                       file.name.toLowerCase().endsWith('.mov');

    if (!isValidType) {
      return { valid: false, error: 'Only .wav, .mp4, and .mov files are allowed' };
    }

    return { valid: true };
  };

  const uploadToSupabase = async (file) => {
    const fileName = `${Date.now()}-${file.name}`;
    const filePath = `uploads/${fileName}`;

    try {
      const { data, error } = await supabase.storage
        .from('media-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('media-files')
        .getPublicUrl(filePath);

      return {
        success: true,
        url: publicUrl,
        path: filePath,
        fileName: file.name,
        type: file.type,
        size: file.size
      };
    } catch (error) {
      console.error('Upload error:', error);
      return {
        success: false,
        error: error.message || 'Upload failed'
      };
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadError('');
    const validation = validateFile(file);
    
    if (!validation.valid) {
      setUploadError(validation.error);
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadToSupabase(file);
      
      if (result.success) {
        const newFile = {
          id: Date.now(),
          name: result.fileName,
          url: result.url,
          path: result.path,
          type: result.type,
          size: result.size,
          isVideo: result.type.startsWith('video/'),
          uploadedAt: new Date().toISOString()
        };

        setUploadedFiles(prev => [...prev, newFile]);
        setUploadError('');
        
        // Auto-select the uploaded file for playback
        if (newFile.isVideo) {
          setCurrentPlayingFile(newFile);
        } else {
          setAudioUrl(newFile.url);
          setUseDefaultDemo(false);
          setAudioFile({ name: newFile.name });
        }
      } else {
        setUploadError(result.error);
      }
    } catch (error) {
      setUploadError('Upload failed. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteFile = async (file) => {
    try {
      const { error } = await supabase.storage
        .from('media-files')
        .remove([file.path]);

      if (error) throw error;

      setUploadedFiles(prev => prev.filter(f => f.id !== file.id));
      
      // If this was the current playing file, reset
      if (currentPlayingFile?.id === file.id) {
        setCurrentPlayingFile(null);
        resetToDefault();
      }
    } catch (error) {
      console.error('Delete error:', error);
      setUploadError('Failed to delete file');
    }
  };

  const playFile = (file) => {
    if (file.isVideo) {
      setCurrentPlayingFile(file);
      setIsPlaying(false); // Stop audio if playing
      if (audioRef.current) {
        audioRef.current.pause();
      }
    } else {
      setAudioUrl(file.url);
      setUseDefaultDemo(false);
      setAudioFile({ name: file.name });
      setCurrentPlayingFile(null);
      // Auto-play audio
      setTimeout(() => {
        togglePlayback();
      }, 100);
    }
  };

  const togglePlayback = async () => {
    if (!audioRef.current) {
      console.error('Audio reference not available');
      return;
    }

    try {
      if (isPlaying) {
        // Pause the audio
        console.log('Pausing audio...');
        audioRef.current.pause();
        // Don't set state here - let the event handler do it
      } else {
        // Play the audio
        console.log('Playing audio...');
        if (audioRef.current.ended) {
          audioRef.current.currentTime = 0;
        }
        
        // Ensure audio is loaded before playing
        if (audioRef.current.readyState < 2) {
          audioRef.current.load();
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Audio load timeout')), 5000);
            audioRef.current.addEventListener('canplay', () => {
              clearTimeout(timeout);
              resolve();
            }, { once: true });
            audioRef.current.addEventListener('error', () => {
              clearTimeout(timeout);
              reject(new Error('Audio load error'));
            }, { once: true });
          });
        }
        
        await audioRef.current.play();
        // Don't set state here - let the event handler do it
      }
    } catch (error) {
      console.error('Audio playback failed:', error);
      setIsPlaying(false);
      setUploadError('Audio playback failed. Please try again.');
    }
  };

  const handleAudioPlay = () => {
    console.log('Audio play event fired');
    setIsPlaying(true);
  };

  const handleAudioPause = () => {
    console.log('Audio pause event fired');
    setIsPlaying(false);
  };

  const handleAudioEnded = () => {
    console.log('Audio ended event fired');
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleAudioError = (e) => {
    console.error('Audio loading error:', e);
    setIsPlaying(false);
    setUploadError('Failed to load audio file');
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const resetToDefault = () => {
    setAudioUrl('https://mzunalhoitapgthoksjn.supabase.co/storage/v1/object/public/site-assets/riverside_ben_linkedin_website.mp3');
    setUseDefaultDemo(true);
    setAudioFile(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentPlayingFile(null);
    setUploadError('');
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.load();
    }
  };

  return (
    <section id="demo" className="py-24 bg-gradient-to-br from-ai-teal to-dental-blue">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-clean-white mb-6">
            Hear{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-growth-green to-energy-coral">
              Donna
            </span>{' '}
            in Action
          </h2>
          <p className="text-xl text-clean-white opacity-90 max-w-4xl mx-auto leading-relaxed">
            Wondering how natural our AI sounds? Listen to a real conversation between a patient and Donna. 
            Experience the future of dental scheduling — effortless, professional, and patient-friendly.
          </p>
        </motion.div>

        {/* Video Player for uploaded videos */}
        {currentPlayingFile?.isVideo && (
          <motion.div
            className="mb-12 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-black rounded-2xl overflow-hidden shadow-2xl">
              <video
                ref={videoRef}
                src={currentPlayingFile.url}
                controls
                className="w-full h-auto max-h-96"
                onError={() => setUploadError('Failed to load video')}
              >
                Your browser does not support video playback.
              </video>
            </div>
            <div className="text-center mt-4">
              <p className="text-clean-white text-lg font-semibold">
                Playing: {currentPlayingFile.name}
              </p>
            </div>
          </motion.div>
        )}

        {/* Audio Player - Only show if no video is playing */}
        {!currentPlayingFile?.isVideo && (
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="relative">
              <motion.div
                className="w-64 h-64 bg-gradient-to-br from-growth-green to-energy-coral rounded-full flex items-center justify-center mx-auto shadow-2xl cursor-pointer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={togglePlayback}
              >
                <SafeIcon 
                  icon={isPlaying ? FiPause : FiPlay} 
                  className="text-clean-white text-6xl ml-2" 
                />
              </motion.div>

              {/* Soundwave Animation */}
              {isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center">
                  {[...Array(8)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1 bg-clean-white mx-1 rounded-full"
                      style={{
                        position: 'absolute',
                        left: `${30 + i * 8}%`,
                        top: '50%',
                        transformOrigin: 'bottom',
                      }}
                      animate={{
                        height: [20, 60, 20],
                        y: [-10, -30, -10],
                      }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        delay: i * 0.1,
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Pulse rings when playing */}
              {isPlaying && [...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute inset-0 rounded-full border-2 border-clean-white"
                  animate={{
                    scale: [1, 1.5, 2],
                    opacity: [0.6, 0.3, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.6,
                  }}
                />
              ))}
            </div>

            <motion.div
              className="mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-3xl font-bold text-clean-white mb-4">
                {isPlaying ? "🎵 Playing Demo" : "Click to Play Demo"}
              </h3>
              
              {/* Audio Progress */}
              {duration > 0 && (
                <div className="bg-clean-white bg-opacity-20 rounded-full h-3 mb-6 max-w-md mx-auto">
                  <div 
                    className="bg-energy-coral h-3 rounded-full transition-all duration-300"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  ></div>
                </div>
              )}
              
              <p className="text-xl text-clean-white opacity-90 mb-8">
                {isPlaying 
                  ? `Playing: ${formatTime(currentTime)} / ${formatTime(duration)}`
                  : useDefaultDemo 
                    ? "Experience natural AI conversation"
                    : "Playing your custom audio"
                }
              </p>

              {/* Hidden Audio Element - FIXED: Better event handling */}
              <audio
                ref={audioRef}
                src={audioUrl}
                onPlay={handleAudioPlay}
                onPause={handleAudioPause}
                onEnded={handleAudioEnded}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onError={handleAudioError}
                className="hidden"
                preload="metadata"
              />
            </motion.div>
          </motion.div>
        )}

        {/* Upload Section */}
        <motion.div
          className="max-w-4xl mx-auto mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".wav,.mp4,.mov,audio/wav,video/mp4,video/quicktime"
            onChange={handleFileUpload}
            className="hidden"
          />

          <div className="text-center mb-8">
            {!useDefaultDemo && !currentPlayingFile && (
              <motion.button
                onClick={resetToDefault}
                className="mt-4 bg-gray-500 text-clean-white px-6 py-3 rounded-xl font-semibold flex items-center space-x-3 justify-center hover:bg-gray-600 transition-all duration-200 shadow-lg mx-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={FiVolume2} className="text-xl" />
                <span>Use Default Demo</span>
              </motion.button>
            )}
          </div>

          {/* Upload Error */}
          {uploadError && (
            <motion.div
              className="mb-6 p-4 bg-red-100 border border-red-300 rounded-xl text-red-700 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="font-semibold">❌ {uploadError}</p>
            </motion.div>
          )}

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <motion.div
              className="bg-clean-white bg-opacity-10 rounded-2xl p-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h4 className="text-xl font-bold text-clean-white mb-4 text-center">
                Uploaded Files
              </h4>
              <div className="space-y-4">
                {uploadedFiles.map((file) => (
                  <motion.div
                    key={file.id}
                    className="bg-clean-white bg-opacity-20 rounded-xl p-4 flex items-center justify-between"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <div className="flex items-center space-x-3">
                      <SafeIcon 
                        icon={file.isVideo ? FiVideo : FiMusic} 
                        className="text-clean-white text-2xl" 
                      />
                      <div>
                        <p className="text-clean-white font-semibold">{file.name}</p>
                        <p className="text-clean-white opacity-70 text-sm">
                          {formatFileSize(file.size)} • {file.isVideo ? 'Video' : 'Audio'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <motion.button
                        onClick={() => playFile(file)}
                        className="bg-growth-green text-clean-white px-4 py-2 rounded-lg font-semibold hover:bg-opacity-80 transition-all duration-200"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <SafeIcon icon={FiPlay} className="text-lg" />
                      </motion.button>
                      <motion.button
                        onClick={() => deleteFile(file)}
                        className="bg-red-500 text-clean-white px-4 py-2 rounded-lg font-semibold hover:bg-red-600 transition-all duration-200"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <SafeIcon icon={FiTrash2} className="text-lg" />
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Features */}
        <motion.div
          className="mt-16 grid md:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          {[
            { icon: FiHeadphones, title: "Natural Conversation", desc: "Human-like interactions" },
            { icon: FiPlay, title: "Real-time Responses", desc: "Instant appointment booking" },
            { icon: FiUpload, title: "Custom Training", desc: "Tailored to your practice" }
          ].map((feature, index) => (
            <div key={feature.title} className="text-center">
              <div className="w-16 h-16 bg-clean-white bg-opacity-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={feature.icon} className="text-clean-white text-2xl" />
              </div>
              <h4 className="text-lg font-semibold text-clean-white mb-2">{feature.title}</h4>
              <p className="text-clean-white opacity-80">{feature.desc}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Demo;